// src/services/AuthServices.jsx
import React from 'react';
import axiosInstance from "../lib/axiosInstance";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:2083";

/**
 * Custom hook for authentication services
 */
export const useAuthServices = () => {
  const loginService = React.useCallback(async (payload) => {
    try {
      console.log("📤 Sending login request to:", `${API_BASE_URL}/user/loginUser`);
      
      const response = await fetch(`${API_BASE_URL}/user/loginUser`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      
      // Check if response status indicates success (2xx)
      if (!response.ok) {
        // Server returned an error with JSON response
        console.error("❌ Server error response:", data);
        return {
          success: false,
          message: data.message || "Login failed",
          status: false
        };
      }
      
      console.log("✅ Login service response received:", data);
      
      // Ensure the response has success field
      return {
        ...data,
        success: data.status || data.success || false
      };
      
    } catch (error) {
      console.error("🚨 Login service error:", error);
      
      // Network or parsing error
      return {
        success: false,
        message: "Network error. Please check your connection.",
        status: false,
        error: error.message
      };
    }
  }, []);

  const verifyToken = React.useCallback(async () => {
    try {
      const response = await axiosInstance.get(`${API_BASE_URL}/user/verify`);
      return response.data;
    } catch (error) {
      console.error("Token verification failed:", error);
      throw error;
    }
  }, []);

  const createAgent = React.useCallback(async (payload) => {
    try {
      console.log("📤 Sending create agent request to:", `${API_BASE_URL}/user/agent`);
      const response = await axiosInstance.post(`${API_BASE_URL}/user/agent`, payload);
      console.log("✅ Create agent service response received");
      return response.data;
    } catch (error) {
      console.error("🚨 Create agent service error:", error);
      return {
        status: false,
        message: error.message || "Agent creation failed",
        error: error.message
      };
    }
  }, []);

  const getAgents = React.useCallback(async () => {
    try {
      console.log("📤 Fetching agents from:", `${API_BASE_URL}/user/agent`);
      const response = await axiosInstance.get(`${API_BASE_URL}/user/agent`);
      console.log("✅ Get agents service response received:", response);
      console.log("✅ Response data:", JSON.stringify(response.data, null, 2));
      console.log("✅ Response status:", response.status);
      return response.data;
    } catch (error) {
      console.error("🚨 Get agents service error:", error);
      console.error("🚨 Error response:", error.response?.data);
      console.error("🚨 Error status:", error.response?.status);
      return {
        status: false,
        message: error.message || "Failed to fetch agents",
        error: error.message
      };
    }
  }, []);

  const updateAgent = React.useCallback(async (payload) => {
    try {
      console.log("📤 Sending update agent request to:", `${API_BASE_URL}/user/agent`);
      console.log("📤 Update payload:", JSON.stringify(payload, null, 2));
      const response = await axiosInstance.put(`${API_BASE_URL}/user/agent`, payload);
      console.log("✅ Update agent service response received:", response);
      console.log("✅ Response data:", JSON.stringify(response.data, null, 2));
      return response.data;
    } catch (error) {
      console.error("🚨 Update agent service error:", error);
      console.error("🚨 Error response:", error.response?.data);
      console.error("🚨 Error status:", error.response?.status);
      return {
        status: false,
        message: error.message || "Agent update failed",
        error: error.message
      };
    }
  }, []);

  return {
    loginService,
    verifyToken,
    createAgent,
    getAgents,
    updateAgent
  };
};

// Standalone functions (for backward compatibility)
export const loginService = async (payload) => {
  try {
    console.log("📤 Sending login request to:", `${API_BASE_URL}/user/loginUser`);
    
    const response = await fetch(`${API_BASE_URL}/user/loginUser`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    
    // Check if response status indicates success (2xx)
    if (!response.ok) {
      // Server returned an error with JSON response
      console.error("❌ Server error response:", data);
      return {
        success: false,
        message: data.message || "Login failed",
        status: false
      };
    }
    
    console.log("✅ Login service response received:", data);
    
    // Ensure the response has success field
    return {
      ...data,
      success: data.status || data.success || false
    };
    
  } catch (error) {
    console.error("🚨 Login service error:", error);
    
    // Network or parsing error
    return {
      success: false,
      message: "Network error. Please check your connection.",
      status: false,
      error: error.message
    };
  }
};

export const verifyToken = async () => {
  try {
    const response = await axiosInstance.get(`${API_BASE_URL}/user/verify`);
    return response.data;
  } catch (error) {
    console.error("Token verification failed:", error);
    throw error;
  }
};

export const createAgent = async (payload) => {
  try {
    console.log("📤 Sending create agent request to:", `${API_BASE_URL}/user/agent`);
    const response = await axiosInstance.post(`${API_BASE_URL}/user/agent`, payload);
    console.log("✅ Create agent service response received");
    return response.data;
  } catch (error) {
    console.error("🚨 Create agent service error:", error);
    return {
      status: false,
      message: error.message || "Agent creation failed",
      error: error.message
    };
  }
};

export const getAgents = async () => {
  try {
    console.log("📤 Fetching agents from:", `${API_BASE_URL}/user/agent`);
    const response = await axiosInstance.get(`${API_BASE_URL}/user/agent`);
    console.log("✅ Get agents service response received:", response);
    console.log("✅ Response data:", JSON.stringify(response.data, null, 2));
    console.log("✅ Response status:", response.status);
    return response.data;
  } catch (error) {
    console.error("🚨 Get agents service error:", error);
    console.error("🚨 Error response:", error.response?.data);
    console.error("🚨 Error status:", error.response?.status);
    return {
      status: false,
      message: error.message || "Failed to fetch agents",
      error: error.message
    };
  }
};

export const updateAgent = async (payload) => {
  try {
    console.log("📤 Sending update agent request to:", `${API_BASE_URL}/user/agent`);
    console.log("📤 Update payload:", JSON.stringify(payload, null, 2));
    const response = await axiosInstance.put(`${API_BASE_URL}/user/agent`, payload);
    console.log("✅ Update agent service response received:", response);
    console.log("✅ Response data:", JSON.stringify(response.data, null, 2));
    return response.data;
  } catch (error) {
    console.error("🚨 Update agent service error:", error);
    console.error("🚨 Error response:", error.response?.data);
    console.error("🚨 Error status:", error.response?.status);
    return {
      status: false,
      message: error.message || "Agent update failed",
      error: error.message
    };
  }
};

// Export as a React Component for Provider pattern
export const AuthServicesProvider = ({ children }) => {
  const services = useAuthServices();
  
  return (
    <AuthServicesContext.Provider value={services}>
      {children}
    </AuthServicesContext.Provider>
  );
};

// Create context for auth services
const AuthServicesContext = React.createContext();

// Hook to use auth services
export const useAuthServicesContext = () => {
  const context = React.useContext(AuthServicesContext);
  if (!context) {
    throw new Error('useAuthServicesContext must be used within AuthServicesProvider');
  }
  return context;
};

export default {
  loginService,
  verifyToken,
  createAgent,
  getAgents,
  updateAgent,
  useAuthServices,
  AuthServicesProvider,
  useAuthServicesContext
};